import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Cookie } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";

export function CookieConsent() {
    const [isVisible, setIsVisible] = useState(false);
    const { t } = useTranslation();

    // Hide on native platforms (iOS/Android) as they have their own permission systems
    // or don't use web-style cookie banners
    if (Capacitor.isNativePlatform()) {
        return null;
    }

    useEffect(() => {
        // Check if user has already made a choice
        const consent = localStorage.getItem("padelo_cookie_consent");
        if (!consent) {
            // Show banner after a small delay
            const timer = setTimeout(() => setIsVisible(true), 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleAccept = () => {
        localStorage.setItem("padelo_cookie_consent", "accepted");
        setIsVisible(false);
        // Here you would typically initialize analytics
    };

    const handleDecline = () => {
        localStorage.setItem("padelo_cookie_consent", "declined");
        setIsVisible(false);
    };

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-[100] p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] md:p-6 animate-in slide-in-from-bottom duration-500">
            <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3 md:gap-4">
                <div className="flex items-start gap-3 flex-1">
                    <div className="hidden md:block p-2 bg-blue-50 rounded-full shrink-0">
                        <Cookie className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="space-y-1">
                        <h3 className="text-sm md:text-base font-semibold text-gray-900 flex items-center gap-2">
                            <Cookie className="w-4 h-4 text-blue-600 md:hidden" />
                            {t('cookies.title', 'Nous utilisons des cookies')}
                        </h3>
                        <p className="text-xs md:text-sm text-gray-600 leading-relaxed max-w-2xl text-justify">
                            {t('cookies.description', 'Nous utilisons des cookies pour améliorer votre expérience utilisateur et analyser le trafic. Vous pouvez accepter ou refuser les cookies non essentiels. En savoir plus dans notre')}
                            {' '}
                            <a href="/assets/website/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">
                                {t('cookies.policyLink', 'Politique de Confidentialité')}
                            </a>.
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto">
                    <Button
                        variant="outline"
                        onClick={handleDecline}
                        size="sm"
                        className="flex-1 md:flex-none border-gray-300 hover:bg-gray-50 h-8 text-xs md:text-sm md:h-10"
                    >
                        {t('cookies.decline', 'Refuser')}
                    </Button>
                    <Button
                        onClick={handleAccept}
                        size="sm"
                        className="flex-1 md:flex-none bg-blue-600 hover:bg-blue-700 text-white h-8 text-xs md:text-sm md:h-10"
                    >
                        {t('cookies.accept', 'Accepter')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
